import React from 'react';
import { Button, Markdown } from '@uplift/ui-v2';
import { CollaborativeAgreementSigned } from '@uplift/api';
import { useAgreementTemplateQuery, useCollaboratePhysicianMatchByIdForPhysicianQuery, useStateQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { Box, Divider, Table, TableBody, TableRow, Typography } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { DetailsCell, DocumentLayout, EffectiveDateCell } from './components';
import { useDownloadAgreement } from './useDownloadAgreement';


export const CollaborativeAgreementCounterSignedPreview: React.VFC<{
  document: CollaborativeAgreementSigned;
}> = ({
  document,
}) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const { isDownloading, downloadAgreement } = useDownloadAgreement();
  const { data: match } = useCollaboratePhysicianMatchByIdForPhysicianQuery(me.id, document.collaborativeMatchId);
  const { data: template } = useAgreementTemplateQuery(document.agreementTemplateId);
  const { data: state } = useStateQuery(match.stateId, { suspense: true });
  const { data: npName } = useUserDisplayNameQuery(match.providerId, { suspense: true });
  const { data: physicianName } = useUserDisplayNameQuery(match.collaborativePhysicianId, { suspense: true });

  return (
    <DocumentLayout
      title={template.name}
      subtitle="sent by uplift"
      controls={(
        <Box>
          <Divider />
          <Box sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={isDownloading}
              startIcon={<FileDownload />}
              onClick={() => {
                const filename = `${state.fullName.replace(/\s+/g, '_')}_collaborative_agreement_${CalendarDateService.date(document.signedAt).format('MM_DD_YYYY')}.pdf`;

                downloadAgreement(document.id, filename);
              }}
            >
              {isDownloading ? 'Preparing...' : 'Download'}
            </Button>
          </Box>
        </Box>
      )}
    >
      <Typography variant="overline" color="secondary">AGREEMENT DETAILS</Typography>
      <Table size="small" sx={{ 'tr:last-of-type td': { borderBottom: 0 } }}>
        <TableBody>
          <TableRow>
            <DetailsCell label="State Covered">{state.fullName}</DetailsCell>
            <DetailsCell label="Status">
              {document.counterSignedAt ? 'Active' : 'Awaiting counter-signature'}
            </DetailsCell>
          </TableRow>
          <TableRow>
            <EffectiveDateCell document={document} />
            <DetailsCell label="Expires">{CalendarDateService.date(document.expireAt, DateFormats.ISO_DATE).tz(timezone).format('MMM D, YYYY')}</DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Nurse Practitioner">{npName.fullLegalName}</DetailsCell>
            <DetailsCell label="Collaborating Physician">{physicianName.fullLegalName}</DetailsCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box sx={{ mt: 2 }}>
        <Typography variant="overline" color="secondary">AGREEMENT TEXT</Typography>
        <Markdown>{document.content}</Markdown>
      </Box>
    </DocumentLayout>
  );
};
